import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { User } from '../../models/auth.model';
import {
  UserSettings,
  WatchList,
  Chart,
  SymbolSelection,
  PublishSchedule,
  SCHEDULE_TIME_ZONES,
  TimeZoneOption,
  normalizeScheduleTimeZoneId
} from '../../models/settings.model';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
private destroy$ = new Subject<void>();
  
currentUser: User | null = null;
  settings: UserSettings | null = null;
  userMenuOpen: boolean = false;
  
  // TradingView credentials form
  sessionId: string = '';
  sessionIdSign: string = '';
  
  // WatchList and Chart selection
  watchLists: WatchList[] = [];
  charts: Chart[] = [];
  selectedWatchListId: number | null = null;
  selectedChartId: number | null = null;
  indicatorTitle: string = '';
  selectedSymbols: string[] = [];
  
  // Symbol selection options
  alwaysFetchLatestSymbols: boolean = true;
  symbolSelections: SymbolSelection[] = [];
  emptyWatchlistError: boolean = false;
  simpleSymbolDisplay: boolean = false;
  
  // UI state
  isLinking: boolean = false;
  isUnlinking: boolean = false;
  isRefreshing: boolean = false;
  isSaving: boolean = false;
  isPublishing: boolean = false;
  isScheduleLoading: boolean = false;
  isScheduleSaving: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  hasUnsavedChanges: boolean = false;
  publishSchedule: PublishSchedule | null = null;
  scheduleEnabled: boolean = false;
  scheduleDays: string[] = [];
  scheduleTime: string = '09:30';
  scheduleTimeZone: string = normalizeScheduleTimeZoneId(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  hasScheduleChanges: boolean = false;
  readonly weekDays: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  readonly timeZoneOptions: TimeZoneOption[] = SCHEDULE_TIME_ZONES;
  private readonly defaultScheduleDays: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  private messageTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private authService: AuthService,
    private settingsService: SettingsService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.authService.currentUser.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.currentUser = user;
    });

    // Get settings from resolver
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.settings = data['settings'];
      this.initializeSelections();
    });

    // Subscribe to settings changes for dynamic updates
    this.settingsService.settings$.pipe(takeUntil(this.destroy$)).subscribe(settings => {
      if (settings) {
        this.settings = settings;
        this.initializeSelections();
      }
    });

    // Subscribe to watchLists changes
    this.settingsService.watchLists$.pipe(takeUntil(this.destroy$)).subscribe(watchLists => {
      this.watchLists = watchLists;
      this.updateSelectedSymbols();
    });

    // Subscribe to charts changes
    this.settingsService.charts$.pipe(takeUntil(this.destroy$)).subscribe(charts => {
      this.charts = charts;
    });

    this.loadPublishSchedule();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    this.errorMessage = '';
    this.autoDismissMessage();
  }

  private showError(message: string): void {
    this.errorMessage = message;
    this.successMessage = '';
    this.autoDismissMessage();
  }

  private autoDismissMessage(): void {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
    this.messageTimeout = setTimeout(() => {
      this.successMessage = '';
      this.errorMessage = '';
    }, 5000);
  }

  private initializeSelections(): void {
    if (this.settings) {
      // Initialize watchLists and charts from settings if available
      if (this.settings.watchLists && this.settings.watchLists.length > 0) {
        this.watchLists = this.settings.watchLists;
      }
      if (this.settings.charts && this.settings.charts.length > 0) {
        this.charts = this.settings.charts;
      }

      // Pre-select previously saved values with type coercion to handle string/number mismatch from API
      this.selectedWatchListId = this.settings.selectedWatchListId != null
        ? Number(this.settings.selectedWatchListId)
        : null;
      this.selectedChartId = this.settings.selectedChartId != null
        ? Number(this.settings.selectedChartId)
        : null;
      this.indicatorTitle = this.settings.indicatorTitle?.trim() || '';

      // Handle selectedWatchListTickers logic
      const tickers = this.settings.selectedWatchListTickers;
      if (!tickers || !Array.isArray(tickers) || tickers.length === 0) {
        this.alwaysFetchLatestSymbols = true;
      } else {
        this.alwaysFetchLatestSymbols = false;
      }

      this.updateSelectedSymbols();

      // Reset unsaved changes flag when initializing
      this.hasUnsavedChanges = false;
    }
  }

  private loadPublishSchedule(): void {
    this.isScheduleLoading = true;
    this.settingsService.getPublishSchedule()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (schedule) => {
          this.isScheduleLoading = false;
          this.initializeSchedule(schedule);
        },
        error: (error) => {
          this.isScheduleLoading = false;
          this.showError(error.error?.message || 'Failed to load publish schedule.');
        }
      });
  }

  private initializeSchedule(schedule: PublishSchedule): void {
    this.publishSchedule = schedule;
    this.scheduleEnabled = schedule.isEnabled;
    this.scheduleDays = schedule.daysOfWeek?.length ? [...schedule.daysOfWeek] : [...this.defaultScheduleDays];
    this.scheduleTime = schedule.timeOfDay || '09:30';
    this.scheduleTimeZone = schedule.hasSchedule === false
      ? normalizeScheduleTimeZoneId(Intl.DateTimeFormat().resolvedOptions().timeZone)
      : normalizeScheduleTimeZoneId(schedule.timeZoneId);
    this.hasScheduleChanges = false;
  }

  onWatchListChange(): void {
    this.updateSelectedSymbols();
    this.hasUnsavedChanges = true;
  }

  onChartChange(): void {
    this.hasUnsavedChanges = true;
  }

  onIndicatorTitleChange(): void {
    this.hasUnsavedChanges = true;
  }

  private updateSelectedSymbols(): void {
    if (this.selectedWatchListId) {
      const selectedWatchList = this.watchLists.find(w => w.id === this.selectedWatchListId);
      this.selectedSymbols = selectedWatchList?.symbols || [];

      // Check for empty watchlist
      this.emptyWatchlistError = this.selectedSymbols.length === 0;

      // If alwaysFetchLatestSymbols is true, select all symbols
      // If false, only select those in selectedWatchListTickers
      let tickers: string[] = [];
      if (this.settings && Array.isArray(this.settings.selectedWatchListTickers)) {
        tickers = this.settings.selectedWatchListTickers;
      }

      this.symbolSelections = this.selectedSymbols.map(symbol => ({
        symbol,
        selected: this.alwaysFetchLatestSymbols ? true : tickers.includes(symbol)
      }));
    } else {
      this.selectedSymbols = [];
      this.symbolSelections = [];
      this.emptyWatchlistError = false;
    }
  }

  onAlwaysFetchChange(): void {
    this.hasUnsavedChanges = true;
  }

  onSymbolSelectionChange(): void {
    this.hasUnsavedChanges = true;
  }

  toggleAllSymbols(selectAll: boolean): void {
    this.symbolSelections.forEach(s => s.selected = selectAll);
    this.hasUnsavedChanges = true;
  }

  get selectedSymbolsCount(): number {
    return this.symbolSelections.filter(s => s.selected).length;
  }

  get hasSelectedSymbols(): boolean {
    return this.alwaysFetchLatestSymbols || this.symbolSelections.some(s => s.selected);
  }

  getDisplaySymbol(symbol: string): string {
    if (this.simpleSymbolDisplay && symbol.includes(':')) {
      return symbol.split(':')[1];
    }
    return symbol;
  }

  trackByWatchListId(index: number, watchList: WatchList): number {
    return watchList.id;
  }

  trackByChartId(index: number, chart: Chart): number {
    return chart.id;
  }

  trackBySymbol(index: number, item: SymbolSelection): string {
    return item.symbol;
  }



  get canSaveSettings(): boolean {
    // Disable save if watchlist is empty
    if (this.emptyWatchlistError) {
      return false;
    }
    // Must have watchlist and chart selected, have unsaved changes, and have at least one symbol selected (or always fetch enabled)
    return this.selectedWatchListId !== null && 
           this.selectedChartId !== null && 
           !!this.indicatorTitle.trim() &&
           this.hasUnsavedChanges &&
           this.hasSelectedSymbols;
  }

  get symbolsText(): string {
    return this.selectedSymbols.join('\n');
  }

  linkTradingViewAccount(): void {
    if (!this.sessionId.trim() || !this.sessionIdSign.trim()) {
      this.showError('Please enter both Session ID and Session ID Sign');
      return;
    }

    this.isLinking = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.settingsService.connectTradingView({
      userSessionId: this.sessionId.trim(),
      userSessionIdSign: this.sessionIdSign.trim()
    }).subscribe({
      next: (response) => {
        this.isLinking = false;
        this.showSuccess(response.message || 'TradingView account linked successfully!');
        this.sessionId = '';
        this.sessionIdSign = '';
        this.loadPublishSchedule();
      },
      error: (error) => {
        this.isLinking = false;
        this.showError(error.error?.message || 'Failed to link TradingView account. Please try again.');
      }
    });
  }

  unlinkAccount(): void {
    this.isUnlinking = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.settingsService.disconnectTradingView().subscribe({
      next: () => {
        this.isUnlinking = false;
        this.showSuccess('Account unlinked successfully!');
        // Reset selections
        this.selectedWatchListId = null;
        this.selectedChartId = null;
        this.selectedSymbols = [];
        this.loadPublishSchedule();
      },
      error: (error) => {
        this.isUnlinking = false;
        this.showError(error.error?.message || 'Failed to unlink account. Please try again.');
      }
    });
  }

  refreshTradingView(): void {
    this.isRefreshing = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.settingsService.refreshTradingView().subscribe({
      next: (response) => {
        this.isRefreshing = false;
        this.showSuccess(response.message || 'Watchlists and charts refreshed successfully! Please select your preferences.');
        // Reset selections to prompt user to re-select
        this.selectedWatchListId = null;
        this.selectedChartId = null;
        this.selectedSymbols = [];
        this.hasUnsavedChanges = false;
      },
      error: (error) => {
        this.isRefreshing = false;
        this.showError(error.error?.message || 'Failed to refresh data. Please try again.');
      }
    });
  }

  saveSettings(): void {
    if (!this.canSaveSettings) {
      if (!this.indicatorTitle.trim()) {
        this.showError('Please enter an Indicator Title');
      } else {
        this.showError('Please select both a WatchList and a Chart');
      }
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Build the request - only include symbols if not always fetching latest
    const request: any = {
      watchListId: this.selectedWatchListId!,
      chartId: this.selectedChartId!,
      indicatorTitle: this.indicatorTitle.trim()
    };

    // Only include symbols if user wants to use specific symbols (not always fetch)
    if (!this.alwaysFetchLatestSymbols) {
      request.symbols = this.symbolSelections
        .filter(s => s.selected)
        .map(s => s.symbol);
    }

    this.settingsService.saveSettings(request).subscribe({
      next: () => {
        this.isSaving = false;
        this.showSuccess('Settings saved successfully!');
        this.hasUnsavedChanges = false;
      },
      error: (error) => {
        this.isSaving = false;
        this.showError(error.error?.message || 'Failed to save settings. Please try again.');
      }
    });
  }

  get canPublish(): boolean {
    return this.settings?.userCredentialsSet === true && this.settings?.adminCredentialsSet === true;
  }

  get showPublishButton(): boolean {
    return this.settings?.userCredentialsSet === true;
  }

  get publishButtonDisabled(): boolean {
    return !this.canPublish || this.isPublishing;
  }

  get showPendingApprovalTip(): boolean {
    return this.settings?.userCredentialsSet === true && this.settings?.adminCredentialsSet === false;
  }

  publishPremiumLevels(): void {
    if (!this.canPublish) {
      return;
    }

    this.isPublishing = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.http.post(`${environment.apiUrl}/Indicator/Publish`, {}).subscribe({
      next: () => {
        this.isPublishing = false;
        this.showSuccess('Indicator Levels have been published successfully!');
      },
      error: () => {
        this.isPublishing = false;
        this.showError('Something went wrong. Please try again later or contact support for further assistance.');
      }
    });
  }

  get publishSettingsReady(): boolean {
    return this.canPublish &&
           this.settings?.selectedWatchListId != null &&
           this.settings?.selectedChartId != null &&
           !!this.settings?.indicatorTitle?.trim();
  }

  get scheduleToggleDisabled(): boolean {
    return this.isScheduleLoading ||
           this.isScheduleSaving ||
           (!this.scheduleEnabled && !this.publishSettingsReady);
  }

  get canSaveSchedule(): boolean {
    if (!this.hasScheduleChanges || this.isScheduleLoading || this.isScheduleSaving) {
      return false;
    }

    if (this.scheduleEnabled) {
      return this.publishSettingsReady &&
             this.scheduleDays.length > 0 &&
             !!this.scheduleTime &&
             !!this.scheduleTimeZone;
    }

    return true;
  }

  get scheduleStatusClass(): string {
    const status = this.publishSchedule?.lastStatus?.toLowerCase();
    if (status === 'succeeded') {
      return 'schedule-status-success';
    }
    if (status === 'failed') {
      return 'schedule-status-error';
    }
    if (status === 'skipped') {
      return 'schedule-status-warning';
    }
    return 'schedule-status-neutral';
  }

  onScheduleChange(): void {
    this.hasScheduleChanges = true;
  }

  onScheduleEnabledChange(): void {
    if (this.scheduleEnabled && !this.publishSettingsReady) {
      this.scheduleEnabled = false;
      this.showError('Please save publish settings and complete admin approval before enabling a schedule.');
      return;
    }

    this.onScheduleChange();
  }

  isScheduleDaySelected(day: string): boolean {
    return this.scheduleDays.includes(day);
  }

  toggleScheduleDay(day: string): void {
    if (this.isScheduleSaving) {
      return;
    }

    if (this.isScheduleDaySelected(day)) {
      this.scheduleDays = this.scheduleDays.filter(d => d !== day);
    } else {
      this.scheduleDays = this.weekDays.filter(d => d === day || this.scheduleDays.includes(d));
    }

    this.onScheduleChange();
  }

  savePublishSchedule(): void {
    if (!this.canSaveSchedule) {
      if (this.scheduleEnabled && this.scheduleDays.length === 0) {
        this.showError('Select at least one schedule day.');
      } else if (this.scheduleEnabled && !this.publishSettingsReady) {
        this.showError('Please save publish settings and complete admin approval before enabling a schedule.');
      }
      return;
    }

    this.isScheduleSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.settingsService.savePublishSchedule({
      isEnabled: this.scheduleEnabled,
      daysOfWeek: this.scheduleDays,
      timeOfDay: this.scheduleTime,
      timeZoneId: this.scheduleTimeZone
    }).subscribe({
      next: (schedule) => {
        this.isScheduleSaving = false;
        this.initializeSchedule(schedule);
        this.showSuccess('Publish schedule saved successfully!');
      },
      error: (error) => {
        this.isScheduleSaving = false;
        this.showError(error.error?.message || 'Failed to save publish schedule.');
      }
    });
  }

  formatScheduleDate(value?: string | null): string {
    if (!value) {
      return 'Not yet';
    }

    return new Intl.DateTimeFormat(undefined, {
      timeZone: this.scheduleTimeZone || undefined,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    }).format(this.parseUtcDate(value));
  }

  private parseUtcDate(value: string): Date {
    const hasExplicitTimeZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
    return new Date(hasExplicitTimeZone ? value : `${value}Z`);
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
  }

  @HostListener('document:click')
  closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.userMenuOpen = false;
  }

  logout(): void {
    this.userMenuOpen = false;
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
